import { dsaToAnnotationToolkit } from '../../../utils/annotationAdapter';
import {
    DEFAULT_REVIEW_FILL_OPACITY,
    REVIEW_USER_COLOR_PALETTE,
} from './reviewDisplayConstants';

/**
 * Clamp a number to [0, 1]. Non-finite values become 1.
 * @param {number} x
 * @returns {number}
 */
export function clamp01(x) {
    if (!Number.isFinite(x)) return 1;
    return Math.min(1, Math.max(0, x));
}

/**
 * True when the feature should not receive fill-opacity or annotator fill coloring —
 * e.g. adapter default `rgba(255, 255, 255, 0)` for "no fill" still sets fillColor, but the
 * toolkit applies explicit fillOpacity over color alpha; forcing opacity to 1 would show a solid fill.
 * @param {Object|null|undefined} properties - GeoJSON feature.properties
 * @returns {boolean}
 */
export function isFillEffectivelyInvisible(properties) {
    if (!properties || typeof properties !== 'object') return true;
    const op = properties.fillOpacity != null ? Number(properties.fillOpacity) : null;
    if (op === 0) return true;

    const fc = properties.fillColor;
    if (fc === undefined || fc === null || fc === '') return true;
    if (typeof fc === 'object' && fc != null && typeof fc.alpha === 'number' && fc.alpha === 0) {
        return true;
    }

    const s = String(fc).trim().toLowerCase();
    if (s === 'transparent' || s === 'none') return true;

    const rgba = s.match(/^rgba?\(\s*[\d.]+\s*,\s*[\d.]+\s*,\s*[\d.]+(?:\s*,\s*([\d.]+)\s*)?\)/);
    if (rgba) {
        const a = rgba[1] !== undefined ? parseFloat(rgba[1]) : 1;
        if (a === 0) return true;
    }

    if (/^#[0-9a-f]{8}$/i.test(s)) {
        const a = parseInt(s.slice(6, 8), 16) / 255;
        if (a === 0) return true;
    }

    return false;
}

/**
 * Deterministic color from user id for legend and annotator-color overlay mode.
 * @param {string|null|undefined} userId
 * @returns {string} hex color
 */
export function colorForUser(userId) {
    let hash = 0;
    const s = String(userId || '');
    for (let i = 0; i < s.length; i += 1) {
        // eslint-disable-next-line no-bitwise
        hash = ((hash << 5) - hash) + s.charCodeAt(i);
        // eslint-disable-next-line no-bitwise
        hash |= 0;
    }
    const idx = Math.abs(hash) % REVIEW_USER_COLOR_PALETTE.length;
    return REVIEW_USER_COLOR_PALETTE[idx];
}

/**
 * Apply admin-review display options to one GeoJSON feature's properties.
 * Always sets userdata.reviewUserLabel for canvas hover tooltips.
 * Does not mutate geometry.
 *
 * @param {Object} feature - GeoJSON feature with optional .properties
 * @param {Object} opts
 * @param {boolean} opts.colorizeByAnnotator - overwrite stroke/fill with userColor when true
 * @param {string} opts.userColor - hex from colorForUser
 * @param {string} opts.reviewUserLabel - display name for tooltips
 * @param {number} opts.fillOpacitySlider - clamped 0–1; in annotator mode sets fill opacity where fill exists; in original mode multiplies existing fill opacity
 */
export function applyReviewFeatureProperties(feature, opts) {
    if (!feature?.properties) return;
    const p = feature.properties;
    const {
        colorizeByAnnotator,
        userColor,
        reviewUserLabel,
        fillOpacitySlider,
    } = opts;

    p.userdata = { ...(p.userdata || {}), reviewUserLabel };

    const hasVisibleFill = !isFillEffectivelyInvisible(p);
    const slider = clamp01(fillOpacitySlider);

    if (colorizeByAnnotator) {
        p.strokeColor = userColor;
        if (hasVisibleFill) {
            p.fillColor = userColor;
            p.fillOpacity = slider;
        }
        return;
    }

    if (hasVisibleFill) {
        const base = p.fillOpacity != null ? Number(p.fillOpacity) : 1;
        const effective = (Number.isFinite(base) ? base : 1) * slider;
        p.fillOpacity = clamp01(effective);
    }
}

/**
 * Build FeatureCollection list for the review overlay from annotations/all API payload.
 * Pure function aside from optional injectable adapter (for tests).
 *
 * @param {Object} params
 * @param {Object|null|undefined} params.overlayData - API body with annotations[] and itemId
 * @param {string|null} params.selectedUserId - filter to one annotator, or null for all
 * @param {boolean} params.colorizeByAnnotator
 * @param {number} [params.fillOpacitySlider=DEFAULT_REVIEW_FILL_OPACITY]
 * @param {typeof dsaToAnnotationToolkit} [params.toToolkit]
 * @returns {Array<Object>} feature collections ready for toolkit.addFeatureCollections
 */
export function buildReviewFeatureCollections({
    overlayData,
    selectedUserId,
    colorizeByAnnotator,
    fillOpacitySlider = DEFAULT_REVIEW_FILL_OPACITY,
    toToolkit = dsaToAnnotationToolkit,
}) {
    const list = overlayData?.annotations || [];
    const toShow = selectedUserId ? list.filter((a) => a.userId === selectedUserId) : list;
    const slider = clamp01(fillOpacitySlider);
    const out = [];

    toShow.forEach((ann) => {
        const dsaDoc = {
            _id: ann.annotationId,
            annotation: {
                name: ann.displayName || ann.login || ann.userId,
                description: '',
                elements: ann.elements || [],
            },
        };
        const fcs = toToolkit(dsaDoc);
        const userColor = colorForUser(ann.userId);
        const reviewUserLabel = ann.displayName || ann.login || ann.userId;

        fcs.forEach((fc) => {
            (fc.features || []).forEach((feature) => {
                applyReviewFeatureProperties(feature, {
                    colorizeByAnnotator,
                    userColor,
                    reviewUserLabel,
                    fillOpacitySlider: slider,
                });
            });
            out.push(fc);
        });
    });

    return out;
}
